import { type JSX, useState } from "react";

import type { InvocationInput, InvocationResult, Operation, VerifyCheck } from "../api/catalog";

interface VerifyPanelProps {
  operation: Operation;
  result: InvocationResult;
  /** What was sent, so a check can carry a value forward from the request as well as the reply. */
  sent: InvocationInput | null;
  /** Open another operation with some fields already filled in. */
  onFollowUp: (operationId: string, prefill: Prefill) => void;
}

/** What to drop into the next operation's form. */
export interface Prefill {
  example: string | null;
  pathParams: Record<string, string>;
  query: Record<string, unknown>;
}

type Verdict = "pass" | "fail" | "info";

/**
 * How to tell whether the call actually did anything.
 *
 * A 2xx is not an effect. The clearest case is the SCM webhook, which answers 202 "accepted"
 * whether it routed the push or dropped it on the floor — only `enqueued` separates the two. So
 * this panel does two things the status code cannot: it checks the fields that carry the real
 * outcome, and it offers the follow-up calls that show the effect, already filled in.
 */
export function VerifyPanel({
  operation,
  result,
  sent,
  onFollowUp,
}: VerifyPanelProps): JSX.Element | null {
  const guidance = operation.verify;
  if (!guidance || !result.ok) {
    // A failure explains itself through the problem document; this panel is about a call that
    // claims to have succeeded.
    return null;
  }

  const signals = guidance.signals
    .map((signal) => {
      const value = readPath(result.data, signal.field);
      return { signal, value, verdict: judge(signal, value) };
    })
    .filter((row) => row.value !== undefined || row.verdict === "fail");

  const failing = signals.filter((row) => row.verdict === "fail");

  return (
    <section className="verify">
      <header className="verify__header">
        <h3>Did it work?</h3>
        {failing.length > 0 && (
          <span className="flag flag--warning">
            {failing.length} {failing.length === 1 ? "signal needs" : "signals need"} a look
          </span>
        )}
      </header>

      {guidance.summary && <p className="verify__summary">{guidance.summary}</p>}

      {signals.length > 0 && (
        <ul className="verify__signals">
          {signals.map(({ signal, value, verdict }) => (
            <li key={signal.field} className={`verify__signal verify__signal--${verdict}`}>
              <span className="verify__mark" aria-hidden="true">
                {verdict === "pass" ? "✓" : verdict === "fail" ? "!" : "·"}
              </span>
              <span className="verify__signal-body">
                <span className="verify__field">
                  <code>{signal.field}</code>
                  <span className="verify__value">{describe(value)}</span>
                </span>
                <span className="muted">{signal.means}</span>
                {verdict === "fail" && signal.when_wrong && (
                  <span className="verify__remedy">{signal.when_wrong}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {guidance.checks.length > 0 && (
        <ol className="verify__checks">
          {guidance.checks.map((check, index) => (
            <CheckRow
              key={`${check.how.slice(0, 40)}:${index}`}
              check={check}
              result={result}
              sent={sent}
              onFollowUp={onFollowUp}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

function CheckRow({
  check,
  result,
  sent,
  onFollowUp,
}: {
  check: VerifyCheck;
  result: InvocationResult;
  sent: InvocationInput | null;
  onFollowUp: (operationId: string, prefill: Prefill) => void;
}): JSX.Element {
  return (
    <li className="verify__check">
      <p>{check.how}</p>

      {check.wait && <p className="verify__wait">{check.wait}</p>}

      <span className="verify__check-actions">
        {check.operation && (
          <button
            type="button"
            className="link-button"
            onClick={() => onFollowUp(check.operation as string, prefill(check, result, sent))}
          >
            Check this →
          </button>
        )}
        {check.command && <CopyCommand command={check.command} />}
      </span>
    </li>
  );
}

function CopyCommand({ command }: { command: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <span className="verify__command">
      <code>{command}</code>
      <button
        type="button"
        className="link-button"
        onClick={() => {
          void navigator.clipboard.writeText(command).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}

/**
 * Work out what to prefill on the follow-up operation.
 *
 * Sources are `data.*` from this response and `path.*` / `query.*` / `body.*` from what was sent.
 * A source that resolves to nothing is left out rather than filled with an empty string, so the
 * follow-up form still shows the field as required instead of looking answered.
 */
function prefill(check: VerifyCheck, result: InvocationResult, sent: InvocationInput | null): Prefill {
  const pathParams: Record<string, string> = {};
  const query: Record<string, unknown> = {};

  for (const [parameter, source] of Object.entries(check.carry)) {
    const [origin, ...rest] = source.split(".");
    const field = rest.join(".");
    const value =
      origin === "data"
        ? readPath(result.data, field)
        : origin === "path"
          ? sent?.path_params?.[field]
          : origin === "query"
            ? sent?.query?.[field]
            : origin === "body"
              ? readPath(sent?.body, field)
              : undefined;

    if (value === undefined || value === null || value === "") {
      continue;
    }
    // Path parameters are always strings; query values keep their type.
    pathParams[parameter] = String(value);
    query[parameter] = value;
  }
  return { example: check.example, pathParams, query };
}

/** Read a dotted path out of a response payload. */
function readPath(source: unknown, path: string): unknown {
  if (!path) {
    return undefined;
  }
  let current: unknown = source;
  for (const step of path.split(".")) {
    if (current === null || typeof current !== "object") {
      return undefined;
    }
    if (Array.isArray(current)) {
      // `data.items.0.id` — point a check at the first row of a list response.
      const index = Number(step);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) {
        return undefined;
      }
      current = current[index];
      continue;
    }
    current = (current as Record<string, unknown>)[step];
  }
  return current;
}

/**
 * Decide whether a signal passed, failed, or is only there to be read.
 *
 * "Informational" is a real third state, not a fallback: an id is always present on a 2xx, so
 * ticking it green would say nothing and marking it red would be a lie. Only a signal that
 * declares an expectation gets a verdict.
 */
function judge(
  signal: { expect: unknown; expect_truthy: boolean; expect_absent: boolean },
  value: unknown,
): Verdict {
  if (signal.expect_absent) {
    return value === null || value === undefined ? "pass" : "fail";
  }
  if (signal.expect_truthy) {
    return isEmpty(value) ? "fail" : "pass";
  }
  if (signal.expect !== null && signal.expect !== undefined) {
    return value === signal.expect ? "pass" : "fail";
  }
  return "info";
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === false || value === 0 || value === "") {
    return true;
  }
  return Array.isArray(value) ? value.length === 0 : false;
}

/** A short, readable rendering of whatever the field held. */
function describe(value: unknown): string {
  if (value === undefined) {
    return "not present";
  }
  if (value === null) {
    return "null";
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "empty" : `${value.length} ${value.length === 1 ? "item" : "items"}`;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>);
    return keys.length === 0 ? "empty" : `${keys.length} fields`;
  }
  const text = String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}
